import { useEffect, useState } from "react";
import { useParams } from "next/navigation";
import { useMutation } from "@tanstack/react-query";
import { useSnapshot } from "valtio/react";
import type { DeviceType } from "utils/src/uaDevicesParse/ua-device-parser";

import { resolvePlaidClientType } from "./plaidPlatform";
import { fetchMemberPlaidLinkToken } from "./memberPlaidApi";
import { isPlaidOAuthRedirect } from "./plaidOAuth";
import { useOspApiClientProvider } from "@/src/hooks/useOspApiClientProvider";
import { rootStore } from "@store/rootStore";
import { useMemoizedFn } from "ahooks";

interface PlaidLinkTokenError {
  error_code: string;
  error_type: string;
  error_message: string;
}

export interface UseCreateLinkTokenOptions {
  /** Fetch link token on mount (requires login). */
  autoInit?: boolean;
}

export interface FetchLinkTokenParams {
  /** Plaid update mode — existing connection internal ID */
  connectionId?: string;
  /** 指定直连的 Broker ID (选 broker 时仅传此参数) */
  brokerId?: number;
  /** 授权过期重新连接时的 account_id (仅 update mode 需要) */
  accountId?: string;
  /** @deprecated 请使用 accountId */
  institutionId?: string;
}

export interface UseCreateLinkTokenResult {
  linkToken: string | null;
  loading: boolean;
  error: PlaidLinkTokenError | null;
  init: (params?: FetchLinkTokenParams) => Promise<string | null>;
  createLinkToken: (params?: FetchLinkTokenParams) => Promise<string | null>;
  /** Clears in-memory link token so react-plaid-link can teardown before a fresh fetch. */
  clearLinkToken: () => void;
}

const toPlaidLinkTokenError = (message: string): PlaidLinkTokenError => ({
  error_code: "MEMBER_API_ERROR",
  error_type: "API_ERROR",
  error_message: message,
});

const persistLinkToken = (token: string | null): void => {
  if (typeof window === "undefined") {
    return;
  }
  if (token) {
    localStorage.setItem("link_token", token);
  }
};

export const useCreateLinkToken = (
  options: UseCreateLinkTokenOptions = {},
): UseCreateLinkTokenResult => {
  const { autoInit = true } = options;
  const { device } = useParams<{ device?: DeviceType }>();
  const { memberApi } = useOspApiClientProvider();
  const { isLoggedIn } = useSnapshot(rootStore.login);

  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [error, setError] = useState<PlaidLinkTokenError | null>(null);

  const memberLinkTokenMutation = useMutation({
    mutationFn: async (params?: FetchLinkTokenParams) => {
      if (!memberApi) {
        throw new Error("memberApi is not available");
      }
      return fetchMemberPlaidLinkToken(
        memberApi,
        resolvePlaidClientType(device),
        {
          connectionId: params?.connectionId,
          brokerId: params?.brokerId,
          accountId: params?.accountId ?? params?.institutionId,
        },
      );
    },
  });

  const createMemberLinkToken = useMemoizedFn(
    async (params?: FetchLinkTokenParams): Promise<string | null> => {
      setError(null);
      try {
        const token = await memberLinkTokenMutation.mutateAsync(params);
        setLinkToken(token);
        persistLinkToken(token);
        return token;
      } catch (err) {
        const message =
          err instanceof Error ? err.message : "Failed to create link token";
        const plaidError = toPlaidLinkTokenError(message);
        setError(plaidError);
        setLinkToken(null);
        return null;
      }
    },
  );

  const createLinkToken = useMemoizedFn(
    async (params?: FetchLinkTokenParams): Promise<string | null> => {
      if (!isLoggedIn) {
        setError(toPlaidLinkTokenError("Login required to connect Plaid"));
        return null;
      }
      return createMemberLinkToken(params);
    },
  );

  const clearLinkToken = useMemoizedFn(() => {
    setLinkToken(null);
  });

  const init = useMemoizedFn(
    async (params?: FetchLinkTokenParams): Promise<string | null> => {
      if (isPlaidOAuthRedirect()) {
        const stored =
          typeof window !== "undefined"
            ? localStorage.getItem("link_token")
            : null;
        setLinkToken(stored);
        return stored;
      }

      if (!isLoggedIn) {
        return null;
      }

      return createLinkToken(params);
    },
  );

  const shouldAutoInit = autoInit && isLoggedIn;

  useEffect(() => {
    if (shouldAutoInit) {
      void init();
    }
  }, [shouldAutoInit, init]);

  return {
    linkToken,
    loading: memberLinkTokenMutation.isPending,
    error,
    init,
    createLinkToken,
    clearLinkToken,
  };
};
